import {
  type HighlightsController,
  type HighlightsRenderer
} from '@databases/highlights';
import { HIGHLIGHT_FILL_PREVIEW_CLASS } from '@databases/highlights/constants';

import { createEffect } from '../utils/create-effect.ts';

import { FILL_PREVIEW_LAYER_Z_INDEX } from './constants.ts';

/**
 * Creates the effect that draws the dashed preview box while the user
 * is dragging the fill handle.
 *
 * Listens to:
 * - `fillPreview`  — draws the dashed box at the current drag extent
 * - `fillApplied`  — clears the dashed box when the fill is committed
 * - `fillStarted`  — clears the dashed box when a new fill drag begins
 * - `fillEnded`    — clears the dashed box when the fill pointer interaction ends
 */
export const createFillPreviewEffect = createEffect(
  (controller: HighlightsController) => {
    // ---------------------------------------------------------------------------
    // DOM
    // ---------------------------------------------------------------------------
    let scrollableContainer: HTMLElement | null = null;

    // ---------------------------------------------------------------------------
    // Renderers
    // ---------------------------------------------------------------------------

    // Dedicated layer for the dashed preview box
    let fillPreviewLayer: HighlightsRenderer | null = null;

    function ensureDependentElementsAndRenderer() {
      if (!scrollableContainer) {
        scrollableContainer = controller.getScrollContainer();
      }

      // Setup the preview layer
      if (!fillPreviewLayer && scrollableContainer) {
        fillPreviewLayer = controller.createLayer();
      }

      return !!(fillPreviewLayer && scrollableContainer);
    }

    return {
      deltaTypes: ['fillStarted', 'fillPreview', 'fillApplied', 'fillEnded'],

      dispose() {
        // Cleanup the preview layer
        if (fillPreviewLayer) {
          fillPreviewLayer.dispose();
          fillPreviewLayer = null;
        }

        scrollableContainer = null;
      },

      onDelta(delta) {
        if (
          !ensureDependentElementsAndRenderer() ||
          !fillPreviewLayer ||
          !scrollableContainer
        ) {
          return;
        }

        // Handle Drawing
        if (delta.kind === 'fillPreview') {
          fillPreviewLayer.clear();

          fillPreviewLayer.add({
            range: {
              id: 'fill-preview-dashed-box',
              anchor: {
                row: delta.fillRange.anchor.row.index,
                col: delta.fillRange.anchor.col.index
              },
              lead: {
                row: delta.fillRange.lead.row.index,
                col: delta.fillRange.lead.col.index
              }
            },
            mode: 'cell',
            zIndex: FILL_PREVIEW_LAYER_Z_INDEX,
            className: HIGHLIGHT_FILL_PREVIEW_CLASS
          });

          return;
        }

        // Handle Clearing
        if (
          delta.kind === 'fillApplied' ||
          delta.kind === 'fillStarted' ||
          delta.kind === 'fillEnded'
        ) {
          fillPreviewLayer.clear();
        }
      }
    };
  }
);
